import { useState } from 'react';
import { Field, SelectField } from '../../components/Field';
import { PageHeader } from '../../components/PageHeader';
import { Panel } from '../../components/Panel';
import { Slider } from '../../components/Slider';
import { useDict } from '../../i18n/dictionary';
import { useLocale } from '../../lib/locale';
import { usePageTitle } from '../../lib/page';
import {
  CUSTOM_MIN,
  DEFAULT_FORM,
  DIMS,
  GRID_SIZES,
  MAX,
  WALLS,
  boxMesh,
  boxName,
  innerSize,
  parseBox,
  type Box,
  type BoxForm,
  type BoxType,
  type Wall,
} from './box';
import { BoxPreview } from './BoxPreview';
import { boxerDict } from './i18n';
import { buildThreeMf } from './threemf';

/** Built and saved in the browser — nothing about a box reaches the API. */
function download(box: Box) {
  const name = boxName(box);
  const bytes = buildThreeMf(name, boxMesh(box));
  const url = URL.createObjectURL(
    new Blob([new Uint8Array(bytes)], { type: 'model/3mf' }),
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = `${name}.3mf`;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * One box type's generator: the form, its preview and the download. Nothing
 * is kept between pages or visits; every open starts from `DEFAULT_FORM`
 * (the routes key each page by type, so switching type remounts it).
 */
export function BoxerPage({ type }: { type: BoxType }) {
  const t = useDict(boxerDict);
  const { locale } = useLocale();
  usePageTitle(t.pageTitle(t[type]));
  const [form, setForm] = useState<BoxForm>(DEFAULT_FORM);
  const { box, invalid } = parseBox(type, form);
  const inner = box && innerSize(box);
  const num = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  // Bare numbers: the field labels already say "(mm)".
  const gridOptions = GRID_SIZES.map((size) => ({
    value: size,
    label: num.format(size),
  }));

  const setDim = (dim: (typeof DIMS)[number], value: string) =>
    setForm((f) => ({ ...f, [dim]: value }));

  return (
    <>
      <PageHeader title={t[type]} />
      <p className="form-hint">{t.intro}</p>

      <div className="boxer-layout">
        <Panel as="div">
          <div className="form-stack">
            {DIMS.map((dim) =>
              type === 'modular' ? (
                <Slider
                  key={dim}
                  id={`boxer-${dim}`}
                  label={t[dim]}
                  options={gridOptions}
                  value={Number(form[dim])}
                  onChange={(size) => setDim(dim, String(size))}
                />
              ) : (
                <div key={dim}>
                  <Field
                    id={`boxer-${dim}`}
                    label={t[dim]}
                    type="number"
                    inputMode="numeric"
                    min={CUSTOM_MIN}
                    max={MAX}
                    step={1}
                    value={form[dim]}
                    onChange={(e) => setDim(dim, e.target.value)}
                    aria-invalid={invalid?.has(dim) || undefined}
                    aria-describedby={
                      invalid?.has(dim) ? `boxer-${dim}-error` : undefined
                    }
                  />
                  {invalid?.has(dim) && (
                    <p id={`boxer-${dim}-error`} className="boxer-field-error">
                      {t.dimensionRange(CUSTOM_MIN, MAX)}
                    </p>
                  )}
                </div>
              ),
            )}

            <SelectField
              id="boxer-wall"
              label={t.wall}
              value={form.wall}
              onChange={(e) =>
                setForm((f) => ({ ...f, wall: Number(e.target.value) as Wall }))
              }
            >
              {WALLS.map((wall) => (
                <option key={wall} value={wall}>
                  {t.wallOption(num.format(wall))}
                </option>
              ))}
            </SelectField>
          </div>
        </Panel>

        <Panel as="div" title={t.preview}>
          {box && inner ? (
            <>
              <BoxPreview box={box} />
              <p className="boxer-inner">
                {t.inner(
                  num.format(inner.x),
                  num.format(inner.y),
                  num.format(inner.z),
                )}
              </p>
              <button
                type="button"
                className="btn-primary-block"
                onClick={() => download(box)}
              >
                {t.download}
              </button>
            </>
          ) : (
            <p className="form-hint">{t.fixFields}</p>
          )}
        </Panel>
      </div>
    </>
  );
}
